import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import Modal from "../../Elements/Modal/Modal";
import MainButton from "../../Elements/MainButton/MainButton";
import SecondButton from "../../Elements/SecondButton/SecondButton";
import { AuthContext } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { fetchAvailableProviders } from "../../../services/PaymentService";

import walletIcon from "../../../assets/icons/cart/wallet.png";
import creditCardIcon from "../../../assets/icons/cart/credit-card.png";
import paypalIcon from "../../../assets/icons/payment-methods/paypal.png";
import blikIcon from "../../../assets/icons/payment-methods/blik-seeklogo.png";
import applePayIcon from "../../../assets/icons/payment-methods/apple-pay.svg";
import googlePayIcon from "../../../assets/icons/payment-methods/g-pay.png";

import "./PaymentModal.css";

const PaymentModal = ({
                        isOpen,
                        onClose,
                        onConfirm,
                        wallet,
                        loading,
                        onlyWallet,
                        geoCountryCode,
                        geoReady,
                        orderTotal,
                        currency = "EUR",
                      }) => {
  const { t, i18n } = useTranslation();
  const { token, authLoading } = useContext(AuthContext);
  const isLoggedIn = Boolean(token);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [providers, setProviders] = useState([]);
  const [fetchingProviders, setFetchingProviders] = useState(false);
  const hasStripe = providers.some((p) => p.code?.toLowerCase() === "stripe");
  const hasPaypal = providers.some((p) => p.code?.toLowerCase() === "paypal");

  const isPolishLang = i18n.language === "pl";
  const showBlik = (isPolishLang || geoCountryCode === "PL") && hasStripe;

  const displayCurrency = wallet?.currency || currency || "EUR";
  const rawBalance = wallet?.mainWallet?.balance ?? wallet?.balance;
  const formattedBalance =
    typeof rawBalance === "number" ? rawBalance.toFixed(2) : Number(rawBalance || 0).toFixed(2);

  useEffect(() => {
    if (!isOpen || onlyWallet) return;

    let isMounted = true;

    const loadProviders = async () => {
      try {
        setFetchingProviders(true);
        const data = await fetchAvailableProviders();
        if (isMounted) {
          setProviders(data?.items || []);
        }
      } catch (err) {
        console.error("Error loading payment providers:", err);
        if (isMounted) {
          // Safe fallback in case service is temporarily unreachable
          setProviders([{ code: "stripe" }, { code: "paypal" }]);
        }
      } finally {
        if (isMounted) setFetchingProviders(false);
      }
    };

    loadProviders();
    return () => {
      isMounted = false;
    };
  }, [isOpen, onlyWallet]);

  useEffect(() => {
    if (!isOpen) return;

    if (onlyWallet || (isLoggedIn && wallet)) {
      setSelectedMethod("wallet");
      return;
    }

    if (hasStripe) {
      setSelectedMethod("card");
    } else if (hasPaypal) {
      setSelectedMethod("paypal");
    } else {
      setSelectedMethod(null);
    }
  }, [isOpen, isLoggedIn, wallet, onlyWallet, hasStripe, hasPaypal]);

  if (!isOpen || authLoading) {
    return null;
  }

  const resolveCheckoutProvider = (method) => {
    if (method === "wallet") return "wallet";
    if (method === "paypal") return "paypal";
    return "stripe";
  };

  const handleConfirm = () => {
    if (!selectedMethod || loading || fetchingProviders) return;
    const provider = resolveCheckoutProvider(selectedMethod);
    onConfirm(provider);
  };

  const isActionDisabled = !selectedMethod || loading || fetchingProviders;
  return (
    <Modal
      isOpen={isOpen}
      onClose={loading ? undefined : onClose}
      title={onlyWallet ? t("cart.payWithWallet", "Pay with Wallet") : t("cart.paymentMethod", "Select Payment Method")}
      actions={
        <>
          <SecondButton onClick={onClose} disabled={loading}>
            {t("common.cancel", "Cancel")}
          </SecondButton>
          <MainButton onClick={handleConfirm} disabled={isActionDisabled} loading={loading}>
            {loading ? t("cart.processing", "Processing...") : t("cart.continueToPay", "Continue")}
          </MainButton>
        </>
      }
    >
      <section className="payment-panel">
        <p className="payment-panel__subtitle">
          {t("cart.payWithWalletSubtitle", "Choose your preferred payment method below.")}
        </p>

        <div className={`payment-methods-group ${loading || fetchingProviders ? "disabled-area" : ""}`}>
          {onlyWallet ? (
            <div className="payment-methods-row payment-methods-row--full">
              <button
                type="button"
                className={`payment-method-tile ${selectedMethod === "wallet" ? "payment-method-tile--active" : ""}`}
                onClick={() => setSelectedMethod("wallet")}
                disabled={loading}
              >
                <img
                  src={walletIcon}
                  alt="Wallet"
                  className="payment-method-tile__icon payment-method-tile__icon--wallet"
                />
                <span className="payment-method-tile__label">
                  {t("cart.wallet", "Wallet")} ({formattedBalance} {displayCurrency})
                </span>
              </button>
            </div>
          ) : (
            <>
              {isLoggedIn && wallet && (
                <div className="payment-methods-row payment-methods-row--full">
                  <button
                    type="button"
                    className={`payment-method-tile ${selectedMethod === "wallet" ? "payment-method-tile--active" : ""}`}
                    onClick={() => setSelectedMethod("wallet")}
                    disabled={loading}
                  >
                    <img
                      src={walletIcon}
                      alt="Wallet"
                      className="payment-method-tile__icon payment-method-tile__icon--wallet"
                    />
                    <span className="payment-method-tile__label">
                      {t("cart.wallet", "Wallet")} ({formattedBalance} {displayCurrency})
                    </span>
                  </button>
                </div>
              )}

              {hasStripe && (
                <div className="payment-methods-row payment-methods-row--two">
                  <button
                    type="button"
                    className={`payment-method-tile payment-method-tile--logo-only ${selectedMethod === "apple_google_pay" ? "payment-method-tile--active" : ""}`}
                    onClick={() => setSelectedMethod("apple_google_pay")}
                    disabled={loading}
                  >
                    <div className="payment-method-tile__logos-inline">
                      <img
                        src={applePayIcon}
                        alt="Apple Pay"
                        className="payment-method-tile__icon payment-method-tile__icon--applepay"
                      />
                      <span className="payment-method-tile__slash">/</span>
                      <img
                        src={googlePayIcon}
                        alt="Google Pay"
                        className="payment-method-tile__icon payment-method-tile__icon--gpay"
                      />
                    </div>
                  </button>

                  <button
                    type="button"
                    className={`payment-method-tile ${selectedMethod === "card" ? "payment-method-tile--active" : ""}`}
                    onClick={() => setSelectedMethod("card")}
                    disabled={loading}
                  >
                    <img
                      src={creditCardIcon}
                      alt="Card"
                      className="payment-method-tile__icon payment-method-tile__icon--card"
                    />
                    <span className="payment-method-tile__label">{t("cart.card", "Card")}</span>
                  </button>
                </div>
              )}

              {(hasPaypal || showBlik) && (
                <div className={`payment-methods-row ${hasPaypal && showBlik ? "payment-methods-row--two" : "payment-methods-row--full"}`}>
                  {hasPaypal && (
                    <button
                      type="button"
                      className={`payment-method-tile payment-method-tile--logo-only ${selectedMethod === "paypal" ? "payment-method-tile--active" : ""}`}
                      onClick={() => setSelectedMethod("paypal")}
                      disabled={loading}
                    >
                      <img
                        src={paypalIcon}
                        alt="PayPal"
                        className="payment-method-tile__icon payment-method-tile__icon--wide"
                      />
                    </button>
                  )}

                  {showBlik && (
                    <button
                      type="button"
                      className={`payment-method-tile payment-method-tile--logo-only ${selectedMethod === "blik" ? "payment-method-tile--active" : ""}`}
                      onClick={() => setSelectedMethod("blik")}
                      disabled={loading}
                    >
                      <img
                        src={blikIcon}
                        alt="BLIK"
                        className="payment-method-tile__icon payment-method-tile__icon--wide"
                      />
                    </button>
                  )}
                </div>
              )}

              {!hasStripe && !hasPaypal && !(isLoggedIn && wallet) && !fetchingProviders && (
                <p className="payment-panel__subtitle">
                  {t("cart.noPaymentMethods", "No payment methods currently available.")}
                </p>
              )}
            </>
          )}
        </div>
      </section>
    </Modal>
  );
};

PaymentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  wallet: PropTypes.object,
  loading: PropTypes.bool,
  onlyWallet: PropTypes.bool,
  geoCountryCode: PropTypes.string,
  geoReady: PropTypes.bool,
  orderTotal: PropTypes.number,
  currency: PropTypes.string,
};

export default PaymentModal;
